import { BehaviorSubject } from 'rxjs/internal/BehaviorSubject';
import {
  bulkCreateLinkData,
  createLinkData,
  deleteLinkData,
  getLink,
  listInvalidLinks,
  listLinkingGroups,
  listLinks,
  patchLink,
  uploadXLFile,
  bulkValidateLinksByAsset,
  LinkDataByAssetRequest
} from '../Api/Link/LinkApi';
import { addError, clearError } from './Error';
import { getAsset, patchAsset } from '../Api/Asset/AssetApi';
import { Asset } from './Asset';
import { User } from './User';
import { LinkEvent } from './LinkEvent';
import { ApiAdvancedFilter, PaginatedResponse } from './GeneralStore';
import { DrawerData } from './Drawer';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type ProgressHandler = (progressEvent: any) => void;

export enum LinkStatus {
  CANDIDATE = 'Candidate',
  ACTIVE = 'Active',
  INACTIVE = 'Inactive',
  REJECTED = 'Rejected'
}
export interface InvalidLink {
  id: string;
  sfdcUpstackId: string;
  assetNumber: string;
  assetSupplier: string;
  rpmAccountId?: number;
  rpmAccountNumber?: string;
  rpmSupplierName?: string;
  asset?: Asset;
}

export interface Link {
  id?: string;
  status: LinkStatus;
  createdBy: User;
  linkEvents: LinkEvent[];
  sfdcUpstackId: string;
  rpmAccountId: number;
  createdAt?: string;
  updatedAt?: string;
  archived?: boolean;
  candidate?: boolean;
  origin?: string;
  //asset
  assetNumber?: string;
  assetTsd?: string;
  assetSupplier?: string;
  assetSupplierName?: string;
  assetCustomer?: string;
  assetCustomerId?: string;
  assetSalesAgent?: string;
  assetCommissionStatus?: string;
  assetStatus?: string;
  assetId?: string;
  assetSfId?: string;
  assetExpectedFirstCommissionRun?: string;
  assetActualFirstCommissionRun?: string;
  assetExpectedDecommissionDate?: string;
  assetActualDecommissionedDate?: string;
  assetActiveLinks?: number;
  //rpm
  rpmAccountNumber?: string;
  rpmAccountCustomerName?: string;
  rpmAccountSupplierName?: string;
  rpmSource?: string;
  rpmAssignedRep?: string;
  rpmAssignedAgency?: string;
  rpmFirstCommissionRun?: string;
  rpmLastCommissionRun?: string;
  rpmAccountActiveLinks?: number;
  // reporting fields
  repeatingOverrideEnabled: boolean;
  repeatingOverridePercent: number | null;
  repeatingOverrideModifiedAt: string | null;
  repeatingOverrideModifiedBy?: User;
}

export interface LinkingGroupItem {
  sfdcUpstackId: string;
  sf_id: string;
  rpmAccountId: number;
  assetSupplierName: string;
  assetNumber: string;
  assetType?: string;
  assetStatus: string;
  commissionStatus: string;
  rpmAccountSupplierName: string;
  rpmAccountNumber: string;
}

export interface UploadError {
  link: Link;
  error: string;
}
export interface FileUploadResponse {
  success: Link[];
  errors: UploadError[];
  message: string;
}

export interface bulkValidateByAssetData {
  request?: LinkDataByAssetRequest;
  new?: Link[];
  notFound?: Link[];
  existing?: Link[];
  conflict?: Link[];
  success?: Link[];
}

export interface LinkAllocation {
  id: string;
  linkId: string;
  tallyRunId: number;
  allocationPercent: number;
  isManualOverride: boolean;
  isSpiffOnly: boolean;
  isItemValueAllocation: boolean;
  revenueRecurringAllocated: number;
  chargeRecurringAllocated: number;
  revenueOnetimeAllocated: number;
  chargeOnetimeAllocated: number;
  rpmRunName: string;
  rpmRunDate: string;
  rpmRunId: string;
  rpmAccountId: number;
  assetUpstackId: string;
  runStatus: string;
  assetNumber: string;
  assetType: string;
  rpmAccountNumber: string;
  linkRepeatingOverrideEnabled?: boolean;
}

const cruxInvalidLinksSubject = new BehaviorSubject<InvalidLink[]>([] as InvalidLink[]);
const cruxInvalidLinksLoadingSubject = new BehaviorSubject<boolean>(false);
const inboundFetchNumberSubject = new BehaviorSubject<number>(0);
const bulkValidateByAssetSubject = new BehaviorSubject<bulkValidateByAssetData>({} as bulkValidateByAssetData);
const bulkValidateByAssetLoadingSubject = new BehaviorSubject<boolean>(false);
const tallyLinksSubject = new BehaviorSubject<PaginatedResponse<Link>>({} as PaginatedResponse<Link>);
const tallyLinksLoadingSubject = new BehaviorSubject<boolean>(false);
const linkSubject = new BehaviorSubject<Link>({} as Link);
const linkLoadingSubject = new BehaviorSubject<boolean>(false);
const linkDetailSubject = new BehaviorSubject<DrawerData | undefined>(undefined);
const cruxLinkingGroupsLoadingSubject = new BehaviorSubject<boolean>(false);
const cruxLinkingGroupsSubject = new BehaviorSubject<LinkingGroupItem[][]>([]);

export const getCruxInvalidLinksSubject = (): BehaviorSubject<InvalidLink[]> => cruxInvalidLinksSubject;
export const getCruxInvalidLinksLoadingSubject = (): BehaviorSubject<boolean> => cruxInvalidLinksLoadingSubject;
export const getCruxLinkingGroupsSubject = (): BehaviorSubject<LinkingGroupItem[][]> => cruxLinkingGroupsSubject;
export const getCruxLinkingGroupsLoadingSubject = (): BehaviorSubject<boolean> => cruxLinkingGroupsLoadingSubject;
export const getTallyLinksSubject = (): BehaviorSubject<PaginatedResponse<Link>> => tallyLinksSubject;
export const getTallyLinksLoadingSubject = (): BehaviorSubject<boolean> => tallyLinksLoadingSubject;
export const getLinkSubject = (): BehaviorSubject<Link> => linkSubject;
export const getLinkLoadingSubject = (): BehaviorSubject<boolean> => linkLoadingSubject;
export const getLinkDetailSubject = (): BehaviorSubject<DrawerData | undefined> => linkDetailSubject;
export const getBulkValidateByAssetSubject = (): BehaviorSubject<bulkValidateByAssetData> => bulkValidateByAssetSubject;
export const getBulkValidateByAssetLoadingSubject = (): BehaviorSubject<boolean> => bulkValidateByAssetLoadingSubject;

export const fetchInvalidLinks = async () => {
  cruxInvalidLinksLoadingSubject.next(true);

  const { data, error } = await listInvalidLinks();

  if (data) {
    clearError('link');
    const dataWithAsset = await Promise.all(
      data.map(async (link) => {
        if (!link.sfdcUpstackId) return link;

        const { data } = await getAsset(link.sfdcUpstackId);
        link.asset = data || undefined;
        return link;
      })
    );
    cruxInvalidLinksSubject.next(dataWithAsset);
  }
  if (error) addError('link', error.message);

  cruxInvalidLinksLoadingSubject.next(false);
};

export const fetchLinkingGroups = async (accountId: string, supplierId?: string) => {
  cruxLinkingGroupsLoadingSubject.next(true);

  const { data, error } = await listLinkingGroups(accountId, supplierId);

  if (data) {
    clearError('link');
    cruxLinkingGroupsSubject.next(data);
  }
  if (error) addError('link', error.message);

  cruxLinkingGroupsLoadingSubject.next(false);
};

export const createLink = async (link: Partial<Link>) => {
  const linkData: Link = { ...({} as Link), ...link };
  const { data, error } = await createLinkData(linkData);
  if (data) clearError('link');
  if (error) addError('link', error.message);
  return { data, error };
};

export const bulkCreateLinks = async (links: Partial<Link>[]) => {
  const linkData: Link[] = links.map((link) => ({ ...({} as Link), ...link }));
  const { data, error } = await bulkCreateLinkData(linkData);
  if (data) clearError('link');
  if (error) addError('link', error.message);
  return { data, error };
};

export const bulkValidateLinksByAssetField = async (linkData: LinkDataByAssetRequest) => {
  bulkValidateByAssetLoadingSubject.next(true);
  bulkValidateByAssetSubject.next({} as bulkValidateByAssetData);
  const { data, error } = await bulkValidateLinksByAsset(linkData);
  if (data) {
    clearError('link');
    bulkValidateByAssetSubject.next({ request: linkData, ...data });
  }
  if (error) {
    addError('link', error.message);
    bulkValidateByAssetSubject.next({} as bulkValidateByAssetData);
  }
  bulkValidateByAssetLoadingSubject.next(false);
};

export const bulkCreateByAssetField = async () => {
  const bulkValidateData = bulkValidateByAssetSubject.getValue();
  const newLinks = bulkValidateData?.new;
  const request = bulkValidateData?.request;
  if (!newLinks || newLinks.length === 0 || !request) return;
  bulkValidateByAssetLoadingSubject.next(true);

  const skippedAssetIds: string[] = [];
  let attemptedAssetUpdates = 0;

  for (const link of newLinks) {
    if (
      !link.assetActualFirstCommissionRun ||
      (request.commissionStatus && request.commissionStatus !== link.assetCommissionStatus)
    ) {
      attemptedAssetUpdates += 1;
      const { data: asset } = await getAsset(link.sfdcUpstackId);
      if (!asset) {
        // A skipped asset update used to leave the surrounding bulk operation reporting success.
        skippedAssetIds.push(link.sfdcUpstackId);
      } else {
        const updateData: Partial<Asset> = {};
        if (!link.assetActualFirstCommissionRun) {
          updateData.actualFirstCommissionRun = request.actualFirstRun || link.rpmFirstCommissionRun || null;
        }
        if (request.commissionStatus && request.commissionStatus !== link.assetCommissionStatus) {
          updateData.commissionsStatus = request.commissionStatus;
        }
        const { data: updatedAsset } = await patchAsset({ ...asset, ...updateData });
        if (!updatedAsset) skippedAssetIds.push(link.sfdcUpstackId);
        else {
          if (updateData.actualFirstCommissionRun)
            link.assetActualFirstCommissionRun = updateData.actualFirstCommissionRun;
          if (updateData.commissionsStatus) link.assetCommissionStatus = updateData.commissionsStatus;
        }
      }
    }
  }

  const linkData: Link[] = newLinks.map((link) => ({
    ...({} as Link),
    sfdcUpstackId: link.sfdcUpstackId,
    rpmAccountId: link.rpmAccountId,
    status: LinkStatus.ACTIVE
  }));

  const { data, error } = await bulkCreateLinkData(linkData, false);
  if (data) {
    clearError('link');
    const createdDataLinks = data.success || [];
    const createdLinks = newLinks.map((link) => {
      const createdLink = createdDataLinks.find((created) => created.sfdcUpstackId === link.sfdcUpstackId);
      return createdLink
        ? {
            ...link,
            id: createdLink.id,
            assetActiveLinks: (link.assetActiveLinks || 0) + 1,
            rpmAccountActiveLinks: (link.rpmAccountActiveLinks || 0) + 1
          }
        : link;
    });
    bulkValidateByAssetSubject.next({ ...bulkValidateData, success: createdLinks, new: [] });
  }
  // One domain key, so a second addError would replace the first rather than add to it.
  const problems: string[] = [];
  if (error) problems.push(error.message);
  if (skippedAssetIds.length > 0) {
    problems.push(
      `${skippedAssetIds.length} of ${attemptedAssetUpdates} asset updates failed; ` +
        'those links were validated without the commission run or status change.'
    );
  }
  if (problems.length > 0) addError('link', problems.join(' '));
  bulkValidateByAssetLoadingSubject.next(false);
};

export const updateLink = async (link: Link, updateDrawer?: boolean) => {
  const { data, error } = await patchLink(link);
  if (error) addError('link', error.message);
  if (data) clearError('link');
  return { data, error };
};

export const deleteLink = async (id: string) => {
  const { data, error } = await deleteLinkData(id);
  if (data) {
    clearError('link');
    tallyLinksSubject.next({
      ...tallyLinksSubject.getValue(),
      data: tallyLinksSubject.getValue().data.filter((link) => link.id !== id)
    });
  }
  if (error) addError('link', error.message);
};

export const uploadMergeFile = (file: File, progressHandler: ProgressHandler) => {
  return uploadXLFile(file, progressHandler);
};

export const fetchLinks = async (filters?: ApiAdvancedFilter, forceRefresh = false) => {
  const newFetchNumber = inboundFetchNumberSubject.getValue() + 1;
  inboundFetchNumberSubject.next(newFetchNumber);
  if (tallyLinksSubject.getValue()?.data?.length > 0 && !forceRefresh) return;
  tallyLinksLoadingSubject.next(true);

  const { data, error } = await listLinks(filters);
  const currentFetchNumber = inboundFetchNumberSubject.getValue();
  if (data && newFetchNumber === currentFetchNumber) {
    clearError('link');
    tallyLinksSubject.next(data);
  }
  if (error) addError('link', error.message);

  newFetchNumber === currentFetchNumber && tallyLinksLoadingSubject.next(false);
};

export const getLinkById = async (id: string): Promise<Link> => {
  const link = tallyLinksSubject?.getValue()?.data?.find((a) => a?.id === id);

  if (link) {
    return link;
  } else {
    const { data, error } = await getLink(id);
    if (error) addError('link', error.message);
    if (data) return data;
  }

  return {} as Link;
};

export const fetchLink = async (id: string, updateDrawer?: boolean): Promise<void | number> => {
  linkLoadingSubject.next(true);

  const { data, error } = await getLink(id);

  if (data) {
    clearError('link');
    const detailSubject = getLinkDetailSubject().getValue() as Link | undefined;
    updateLinkOnTallyLinksSubject(data);
    if (updateDrawer || detailSubject?.id === data.id) updateLinkDetail(data);
    const subject = getLinkSubject().getValue();
    if (!updateDrawer || !subject.id || subject.id === data.id) linkSubject.next(data);
  }
  if (error) {
    addError('link', error.message);
    const statusCode = error.errorResponse?.status || 500;
    return statusCode;
  }

  linkLoadingSubject.next(false);
};

const updateLinkOnTallyLinksSubject = (updatedLink: Link) => {
  const currentLinks = tallyLinksSubject.getValue()?.data || [];
  const updatedLinks = currentLinks.map((link) => (link.id === updatedLink.id ? updatedLink : link));
  tallyLinksSubject.next({ ...tallyLinksSubject.getValue(), data: updatedLinks });
};

export const updateLinkDetail = (linkData: DrawerData) => linkDetailSubject.next(linkData);
